import styled from 'styled-components';
import { useEffect, useState } from 'react';
import useReportDataContext from '../../hooks/useReportDataContext';

type IAppliedFilterCategory = {
  id: string;
  name: string;
  value?: string;
  items: IAppliedFilterItem[];
  type: 'column' | 'fts';
};

type IAppliedFilterItem = {
  id: string;
  name: string;
  value?: string;
};

const ReportAppliedFiltersSection = () => {
  const {
    selectedColumnFilters,
    selectedFtsFilters,
    resetColumnFilters,
    resetFtsFilters,
    removeColumnFilterValue,
    removeFtsFilter
  } = useReportDataContext();

  useEffect(() => {
    const columnAppliedFilters = selectedColumnFilters.map((filter) => ({
      id: filter.key,
      name: filter.name,
      type: 'column' as const,
      items: filter.values.map((value) => ({
        id: value.id,
        name: value.name,
        value: value.name
      }))
    }));

    const ftsAppliedFilters = selectedFtsFilters.map((filter) => ({
      id: filter.key,
      name: filter.key.charAt(0).toUpperCase() + filter.key.slice(1), // Capitalize first letter
      type: 'fts' as const,
      items: [
        {
          id: filter.key,
          name: filter.value,
          value: filter.value
        }
      ]
    }));

    setAppliedFilters([...columnAppliedFilters, ...ftsAppliedFilters]);
  }, [selectedColumnFilters, selectedFtsFilters]);

  const [appliedFilters, setAppliedFilters] = useState<IAppliedFilterCategory[]>([]);

  const handleRemoveFilterItem = (
    filterKey: string,
    itemId: string,
    filterType: 'column' | 'fts'
  ) => {
    if (filterType === 'column') {
      removeColumnFilterValue(filterKey, itemId);
    } else {
      removeFtsFilter(filterKey);
    }
  };

  const handleRemoveEntireFilter = (filterKey: string, filterType: 'column' | 'fts') => {
    if (filterType === 'column') {
      // Remove all values for this filter by getting the current filter and removing all its values
      const currentFilter = selectedColumnFilters.find((f) => f.key === filterKey);
      if (currentFilter) {
        currentFilter.values.forEach((value) => {
          removeColumnFilterValue(filterKey, value.id);
        });
      }
    } else {
      removeFtsFilter(filterKey);
    }
  };

  const handleResetFilters = () => {
    resetColumnFilters();
    resetFtsFilters();
  };

  if (appliedFilters.length === 0) return null;

  return (
    <Wrapper className="applied-filters-section">
      <div className="applied-filters-header">
        <h3>Filtering data by:</h3>
        <button className="reset-button" onClick={handleResetFilters}>
          <span>Reset All</span>
        </button>
      </div>
      <div className="applied-filters-content">
        {appliedFilters.map((filter) => (
          <div className="filter-category" key={filter.id}>
            <div className="filter-category-header">
              <div className="filter-category-name">
                {filter.name}
                {filter.type === 'fts' && <span className="filter-type-badge">Search</span>}:
              </div>
              <button
                className="remove-category-button"
                onClick={() => handleRemoveEntireFilter(filter.id, filter.type)}
                title={`Remove all ${filter.name} filters`}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 1L1 9M1 1L9 9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="filter-tags">
              {filter.items.map((item) => (
                <div
                  className={`filter-tag ${filter.type === 'fts' ? 'fts-tag' : 'column-tag'}`}
                  key={item.id}
                >
                  <span>{filter.type === 'fts' ? `"${item.name}"` : item.name}</span>
                  <button
                    className="remove-button"
                    onClick={() => handleRemoveFilterItem(filter.id, item.id, filter.type)}
                    title={`Remove ${item.name} filter`}
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 1L1 9M1 1L9 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Wrapper>
  );
};

export default ReportAppliedFiltersSection;

const Wrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  padding: 20px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.widget_group.background};
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.05),
    0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.border_color};

  .applied-filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
      color: ${({ theme }) => theme.text};
    }
  }

  .applied-filters-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .filter-category {
    display: flex;
    flex-direction: column;
  }

  .filter-category-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .filter-category-name {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .filter-type-badge {
    background: ${({ theme }) => theme.widget.tableHeader};
    color: ${({ theme }) => theme.primary_color};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .remove-category-button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.text};
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s;
    opacity: 0.7;

    &:hover {
      background-color: rgba(220, 53, 69, 0.1);
      color: #dc3545;
      opacity: 1;
    }
  }

  .filter-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .reset-button {
    background-color: ${({ theme }) => theme.primary_color};
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .filter-tag {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;

    &.column-tag {
      background-color: ${({ theme }) => theme.text};
      color: ${({ theme }) => theme.widget.background};

      &:hover {
        opacity: 0.9;
      }
    }

    &.fts-tag {
      background-color: ${({ theme }) => theme.primary_color};
      color: #ffffff;

      &:hover {
        opacity: 0.9;
      }
    }

    span {
      margin-right: 8px;
    }

    .remove-button {
      background: none;
      border: none;
      color: currentColor;
      padding: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background-color 0.2s;
      opacity: 0.8;

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
        opacity: 1;
      }
    }
  }
`;
